/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState, useRef, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Link, RouteComponentProps, useLocation} from 'react-router-dom'
import {KTSVG} from '../../../_metronic/helpers'
import './OrderDetail.css'
import {PageTitle} from '../../../_metronic/layout/core'
import OrderInfoBox from './components/orderInfoBox'
import ItemTable from './components/itemTable'
import ItemGrid from './components/itemGrid'
import {useDispatch} from 'react-redux'
import {
  filterOrderItemsChange,
  getAiServiceStatus,
  getDetail,
  selectAiServiceStatus,
  selectCurrentOrder,
  selectCurrentOrderItems,
  selectCurrentOrderItemsFilter,
  selectSelectedItems,
  selectSelectedMode,
  setAsyncWorkingOrders,
  setSelectMode,
  updateCurrent,
  updateCurrentOrderItems,
} from './redux/OrderRedux'
import {useAppSelector} from '../../../setup/redux/hooks'
import {OrderModel} from './models/OrderModel'
import {listenOrderDetailChange, listenOrderItemsChange} from './firebase/order'
import {OrderItemModel} from './models/OrderItemModel'
import {off} from 'firebase/database'
import OrderActionBox from './components/OrderActionBox'
import {ORDER_ACTION_VIEW_MODE, OrderActionType, OrderStatus} from './constants'
import Tooltip from 'react-tooltip-lite'
import InfiniteScroll from 'react-infinite-scroll-component'
import {toast} from 'react-toastify'
import {OrderActionModel} from './models/OrderActionModel'
import {getIntl} from '../../shared/intl'

interface RouteParams {
  id: string
}
const enum OrderDetailViewMode {
  List = 'List',
  Grid = 'Grid',
}
interface Props extends RouteComponentProps<RouteParams> {}
const OrderDetail: FC<Props> = (props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [orderViewMode, setViewMode] = useState(OrderDetailViewMode.List)
  const [processingText, setProcessingText] = useState(0)
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null)
  const didRequest = useRef(false)

  // Translatable preparing messages (when completedItems = 0)
  const preparingMessages = [
    'ORDER.PROCESSING.UPLOADING_IMAGES',
    'ORDER.PROCESSING.ANALYSING_IMAGES',
    'ORDER.PROCESSING.PREPROCESSING_IMAGES',
  ]

  // Translatable processing messages (when completedItems > 0)
  const processingMessages = [
    'ORDER.PROCESSING.AI_PROCESSING_IMAGES',
    'ORDER.PROCESSING.SERVER_OVERLOAD_MESSAGE',
  ]
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const downloadUrl = queryParams.get('downloadUrl')
  const orderId = props.match.params.id
  const isAiServerOverload = useAppSelector(selectAiServiceStatus)
  // let orderListener: DatabaseReference | undefined = undefined
  // let itemListener: DatabaseReference | undefined = undefined

  const order: OrderModel | undefined = useAppSelector(selectCurrentOrder)
  const orderItems = useAppSelector(selectCurrentOrderItems)
  const orderItemsFilter = useAppSelector(selectCurrentOrderItemsFilter)
  const selectedMode = useAppSelector(selectSelectedMode)

  const selectedItems = useAppSelector(selectSelectedItems)
  const fetchData = () => {
    dispatch(filterOrderItemsChange({...orderItemsFilter, page: Number(orderItemsFilter.page) + 1}))
  }

  const handleOrderChangeFromFirebase = (updatedOrder: OrderModel) => {
    dispatch(updateCurrent(updatedOrder))
  }
  const handleOrderItemsChangeFromFirebase = (items: OrderItemModel[]) => {
    dispatch(updateCurrentOrderItems(items))
  }
  // Sequential text display for AI processing
  useEffect(() => {
    if (order && order.status === OrderStatus.AI_PROCESSING) {
      // Set processing start time when first entering AI_PROCESSING status
      if (processingStartTime === null) {
        setProcessingStartTime(Date.now())
      }

      if (order.completedItems === 0) {
        // Preparing phase: show messages sequentially
        const interval = setInterval(() => {
          setProcessingText((prev) => {
            const nextIndex = prev + 1
            return nextIndex >= preparingMessages.length ? preparingMessages.length - 1 : nextIndex
          })
        }, 3000) // Change text every 3 seconds

        return () => clearInterval(interval)
      } else {
        // Processing phase: show AI processing message, then check for server overload
        setProcessingText(0) // Start with AI processing message

        const currentTime = Date.now()
        const timeThreshold = order.totalItems <= 10 ? 60000 : 180000 // 1 min or 3 min

        if (processingStartTime && currentTime - processingStartTime > timeThreshold) {
          setProcessingText(1) // Show server overload message
        } else {
          // Set timeout to show overload message after threshold
          const remainingTime = processingStartTime
            ? timeThreshold - (currentTime - processingStartTime)
            : timeThreshold
          const timeout = setTimeout(() => {
            setProcessingText(1)
          }, Math.max(0, remainingTime))

          return () => clearTimeout(timeout)
        }
      }
    } else {
      // Reset when not in AI processing state
      setProcessingStartTime(null)
      setProcessingText(0)
    }
  }, [order, preparingMessages.length, processingStartTime])

  useEffect(() => {
    if (!didRequest.current) {
      dispatch(getAiServiceStatus())
      dispatch(getDetail(orderId))
      if (downloadUrl) {
        const intl = getIntl()
        let downloadToast = toast.info(intl.formatMessage({id: `MESSAGE.ORDER.DOWNLOADING`}), {
          hideProgressBar: true,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: false,
        })

        const orderAction = {
          orderId: Number(orderId),
          type: OrderActionType.Download,
          asyncListenerPath: downloadUrl,
          toast: downloadToast,
        } as OrderActionModel
        dispatch(setAsyncWorkingOrders(orderAction))
      }
    }
    const orderListener = listenOrderDetailChange(orderId, (order: OrderModel) => {
      if (order && order.status) {
        handleOrderChangeFromFirebase(order)
      }
    })
    const itemListener = listenOrderItemsChange(orderId, (items: OrderItemModel[]) => {
      if (items) {
        const orderItems = Object.values(items)
        if (orderItems.length > 0) {
          handleOrderItemsChangeFromFirebase(orderItems)
        }
      }
    })
    return () => {
      didRequest.current = true
      if (itemListener) {
        off(itemListener)
      }
      if (orderListener) {
        off(orderListener)
      }
    }
  }, [downloadUrl, orderId])

  return (
    <>
      {order && (
        <PageTitle
          breadcrumbs={[
            {
              title: intl.formatMessage({id: 'ORDER.LIST'}),
              path: '/orders',
              isSeparator: false,
              isActive: true,
            },
            {
              title: 'dash',
              path: 'dash',
              isSeparator: true,
              isActive: true,
            },
          ]}
        >
          {order.name}
        </PageTitle>
      )}
      {order && order.status === OrderStatus.AI_PROCESSING && isAiServerOverload && (
        <div className='mx-1 mb-5'>
          <div className='d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
            <i className='bi bi-exclamation-circle-fill fs-2tx text-warning me-4'></i>
            <div className='d-flex flex-stack flex-grow-1'>
              <div className='fw-bold'>
                <h4 className='text-gray-800 fw-bolder'>
                  {intl.formatMessage({id: 'Order.ai.overloaded.title'})}
                </h4>
                <div className='fs-6 text-gray-600'>
                  {intl.formatMessage({id: 'Order.ai.overloaded'})}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {order && (
        <div className='row'>
          <div className='col-xl-9'>
            <div className='card'>
              <div className='card-header'>
                <h3 className='card-title fw-bold text-dark'>
                  {intl.formatMessage({id: 'OrderDetail.header.ORDER_ITEMS'})}
                  {order.status === OrderStatus.AI_PROCESSING &&
                    '    (' + order.completedItems + '/' + order.totalItems + ')'}
                  {selectedMode &&
                    ' - ' +
                      intl.formatMessage({id: 'ORDER.ACTIONS.SELECT'}) +
                      ' ' +
                      selectedItems.length +
                      '/' +
                      order.totalItems}
                </h3>
                <div className='card-toolbar'>
                  <OrderActionBox order={order} viewMode={ORDER_ACTION_VIEW_MODE.MINIFY} />
                  {[OrderStatus.COMPLETED,OrderStatus.PENDING,OrderStatus.AI_FAILED].includes(order.status)
                    && (
                    <Tooltip
                      content={intl.formatMessage({id: 'ORDER.ACTIONS.SELECT'})}
                      className='tooltips'
                    >
                      <button
                        type='button'
                        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 p-1'
                        onClick={() => {
                          dispatch(setSelectMode(!selectedMode))
                        }}
                      >
                        <KTSVG
                          path={'/media/icons/duotune/coding/cod006.svg'}
                          className='svg-icon svg-icon-2x svg-icon-info'
                        />
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip
                    content={intl.formatMessage({id: 'ORDER.ACTIONS.BACK'})}
                    className='tooltips'
                  >
                    <Link to={'/orders'} className='btn btn-icon btn-sm btn-secondary ms-1'>
                      <i className='bi fs-1 bi-arrow-left-short'></i>
                    </Link>
                  </Tooltip>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary'
                    onClick={() =>
                      setViewMode(
                        orderViewMode === OrderDetailViewMode.List
                          ? OrderDetailViewMode.Grid
                          : OrderDetailViewMode.List
                      )
                    }
                  >
                    <KTSVG
                      path='/media/icons/duotune/general/gen008.svg'
                      className=' svg-icon svg-icon-2x svg-icon-dark'
                    />
                  </button>
                </div>
              </div>

              {/* AI Processing Progress Bar - Above Items */}
              <div
                className='ai-processing-container'
                style={{
                  display:
                    order.status === OrderStatus.AI_PROCESSING && order.totalItems > 2
                      ? 'block'
                      : 'none',
                }}
              >
                <div className='progress h-20px bg-light position-relative ai-progress-container'>
                  <div
                    className='progress-bar bg-gradient-primary ai-progress-bar'
                    role='progressbar'
                    style={{
                      width: `${
                        order.totalItems > 0
                          ? Math.round((order.completedItems / order.totalItems) * 100)
                          : 0
                      }%`,
                    }}
                    aria-valuenow={
                      order.totalItems > 0
                        ? Math.round((order.completedItems / order.totalItems) * 100)
                        : 0
                    }
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                  {/* Progress percentage overlay */}
                  <div className='position-absolute w-100 h-100 d-flex align-items-center justify-content-center'>
                    <span className='text-dark fs-6 fw-bold'>
                      {order.totalItems > 0
                        ? Math.round((order.completedItems / order.totalItems) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                </div>
                <div className='d-flex justify-content-center mt-3'>
                  <span className='text-primary fs-7 fw-bold d-flex align-items-center'>
                    <i className='fas fa-cog me-2 processing-cog'></i>
                    <span className='processing-text' key={processingText}>
                      {order.completedItems === 0
                        ? intl.formatMessage({
                            id: preparingMessages[
                              Math.min(processingText, preparingMessages.length - 1)
                            ],
                          })
                        : intl.formatMessage({
                            id: processingMessages[
                              Math.min(processingText, processingMessages.length - 1)
                            ],
                          })}
                    </span>
                  </span>
                </div>
              </div>

              <div className='card-body p-5'>
                <InfiniteScroll
                  dataLength={orderItems.length} //This is important field to render the next data
                  next={fetchData}
                  style={{
                    overflowX: 'hidden',
                  }}
                  hasMore={orderItems.length !== orderItemsFilter.itemCount}
                  loader={
                    <h4 className='mt-5' style={{textAlign: 'center'}}>
                      {/* {intl.formatMessage({id: 'SYSTEM.LOADING'})} */}
                    </h4>
                  }
                  // endMessage={
                  //   <p className='mt-3' style={{textAlign: 'center'}}>
                  //     <b>{intl.formatMessage({id: 'SYSTEM.TABLE.SEEN_ALL'})}</b>
                  //   </p>
                  // }
                >
                  {orderViewMode === OrderDetailViewMode.List ? (
                    <ItemTable items={orderItems} />
                  ) : (
                    <ItemGrid items={orderItems} order={order} />
                  )}
                </InfiniteScroll>
              </div>
            </div>
          </div>
          <div className=' col-xl-3'>
            <OrderInfoBox order={order} />
          </div>
        </div>
      )}
    </>
  )
}

export {OrderDetail}
